/**
 * Pure logic for the Project Kanban board.
 *
 * The primary dashboard surface per UX §6. Projects are grouped into
 * five columns by lifecycle state:
 *
 *   ┌──────────┬─────────────┬─────────┬──────────────┬────────┐
 *   │   New    │   Pending   │  Active │   Awaiting   │ Closed │
 *   │  (DFA*)  │   approval  │         │   payment    │        │
 *   └──────────┴─────────────┴─────────┴──────────────┴────────┘
 *
 * Column mapping (more lifecycle states than columns, so we collapse):
 *   - "New":              DFA_RECEIVED, DFA_SUBMITTED, CONTRACT_SIGNED
 *   - "Pending approval": PENDING_PARTS_APPROVAL
 *   - "Active":           ACTIVE
 *   - "Awaiting payment": AWAITING_PAYMENT
 *   - "Closed":           CLOSED (hidden by default; opt-in toggle)
 *
 * Within each column, projects are ordered by lastUpdatedAt DESC so the
 * most-recently-touched items float to the top. Overdue projects render
 * a warning chip; pending-approval projects render a "Needs review" chip
 * for admins.
 *
 * No drag-and-drop in v1. The Kanban is a viewing surface; lifecycle
 * transitions still happen via explicit actions on the project detail
 * page (approve / reject / close, etc.).
 */

import type { ProjectLifecycleState, VisibilityRow } from './visibility-queries.js';

export type KanbanColumnId = 'NEW' | 'PENDING' | 'ACTIVE' | 'AWAITING_PAYMENT' | 'CLOSED';

export interface KanbanColumn {
  id: KanbanColumnId;
  label: string;
  /** Lifecycle states that map into this column. */
  states: readonly ProjectLifecycleState[];
  /** Projects in this column, sorted by lastUpdatedAt DESC. */
  projects: VisibilityRow[];
  /** Per-column metrics shown in the column header. */
  count: number;
  overdueCount: number;
}

export interface KanbanBoard {
  columns: readonly KanbanColumn[];
  totalProjects: number;
  totalOverdue: number;
}

const COLUMN_DEFS: ReadonlyArray<{
  id: KanbanColumnId;
  label: string;
  states: readonly ProjectLifecycleState[];
}> = [
  { id: 'NEW', label: 'New', states: ['DFA_RECEIVED', 'DFA_SUBMITTED', 'CONTRACT_SIGNED'] },
  { id: 'PENDING', label: 'Pending approval', states: ['PENDING_PARTS_APPROVAL'] },
  { id: 'ACTIVE', label: 'Active', states: ['ACTIVE'] },
  { id: 'AWAITING_PAYMENT', label: 'Awaiting payment', states: ['AWAITING_PAYMENT'] },
  { id: 'CLOSED', label: 'Closed', states: ['CLOSED'] },
];

/**
 * Build the Kanban representation from a flat list of visibility rows.
 *
 * `includeClosed` controls whether the CLOSED column is present at all.
 * When false, CLOSED projects don't appear anywhere — matches the default
 * "exclude closed" behaviour on the dashboard.
 */
export function buildKanbanBoard(
  rows: readonly VisibilityRow[],
  options: { includeClosed?: boolean } = {},
): KanbanBoard {
  const includeClosed = options.includeClosed ?? false;

  // Map lifecycle state → column id, computed once.
  const stateToColumn = new Map<ProjectLifecycleState, KanbanColumnId>();
  for (const def of COLUMN_DEFS) {
    for (const s of def.states) stateToColumn.set(s, def.id);
  }

  // Bucket the rows by column id, skipping CLOSED when not requested.
  const buckets = new Map<KanbanColumnId, VisibilityRow[]>();
  for (const r of rows) {
    if (!includeClosed && r.isClosed) continue;
    const col = stateToColumn.get(r.lifecycleState);
    if (!col) continue; // unknown state — defensive
    if (!includeClosed && col === 'CLOSED') continue;
    const arr = buckets.get(col) ?? [];
    arr.push(r);
    buckets.set(col, arr);
  }

  // Sort each bucket by lastUpdatedAt DESC.
  for (const arr of buckets.values()) {
    arr.sort((a, b) => b.lastUpdatedAt.localeCompare(a.lastUpdatedAt));
  }

  const columns: KanbanColumn[] = COLUMN_DEFS
    .filter((def) => includeClosed || def.id !== 'CLOSED')
    .map((def) => {
      const projects = buckets.get(def.id) ?? [];
      return {
        id: def.id,
        label: def.label,
        states: def.states,
        projects,
        count: projects.length,
        overdueCount: projects.filter((p) => p.isOverdue).length,
      };
    });

  const totalProjects = columns.reduce((acc, c) => acc + c.count, 0);
  const totalOverdue = columns.reduce((acc, c) => acc + c.overdueCount, 0);

  return { columns, totalProjects, totalOverdue };
}

/**
 * Filter rows by overdue-only flag (kept separate from the column build so
 * the same logic can be re-used by other surfaces).
 */
export function applyOverdueFilter(
  rows: readonly VisibilityRow[],
  overdueOnly: boolean,
): VisibilityRow[] {
  if (!overdueOnly) return [...rows];
  return rows.filter((r) => r.isOverdue);
}
